import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatCard } from "@/components/app/PageBits";
import { fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import OverdueRemindersButton from "@/components/app/OverdueRemindersButton";

// Wrap StatCard in a Link when `to` is provided so the whole card acts as a drill-down.
function LinkStat({ to, ...props }) {
  const card = <StatCard {...props} />;
  if (!to) return card;
  return <Link to={to} className="block focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-md">{card}</Link>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/dashboard/stats"); setStats(r.data); }
    catch (e) { toast.error("Failed to load stats"); }
  };
  useEffect(() => { load(); }, []);

  const runCron = async () => {
    setBusy(true);
    try {
      const r = await api.post("/cron/generate-due-invoices");
      toast.success(`Generated ${r.data.count} invoice(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const seed = async () => {
    try { const r = await api.post("/seed/demo"); if (r.data.skipped) toast.info(r.data.message); else toast.success("Seed data created"); load(); }
    catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader
        title={user?.active_project ? `${user.active_project.company_name || user.active_project.name} · Dashboard` : "Dashboard"}
        subtitle={user?.active_project
          ? `Live totals for ${user.active_project.company_name || user.active_project.name} only. Switch domains to view other projects.`
          : "A calm summary of billed, paid and outstanding maintenance across all projects and plots."}
        actions={
          <>
            <Button data-testid="seed-demo-btn" variant="outline" onClick={seed}><Sparkles className="h-4 w-4 mr-2"/>Seed demo</Button>
            <OverdueRemindersButton/>
            <Button data-testid="run-cron-btn" onClick={runCron} disabled={busy}>Generate due invoices</Button>
          </>
        }
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <LinkStat to="/invoices"                          testId="stat-billed"       label="Total billed"      value={fmtINR(stats?.totals?.billed)}       tone="default" />
        <LinkStat to="/payments"                          testId="stat-paid"         label="Total paid"        value={fmtINR(stats?.totals?.paid)}         tone="good" />
        <LinkStat to="/invoices?outstanding=1"            testId="stat-outstanding"  label="Outstanding"       value={fmtINR(stats?.totals?.outstanding)}  tone="bad" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <LinkStat to="/customers"                          testId="stat-customers"        label="Customers"        value={stats?.counts?.customers ?? "-"} />
        <LinkStat to="/plots"                              testId="stat-plots"            label="Plots"            value={stats?.counts?.plots ?? "-"} />
        <LinkStat to="/plots?status=active"                testId="stat-active-plots"     label="Active plots"     value={stats?.counts?.active_plots ?? "-"} />
        <LinkStat to="/plots?status=paused"                testId="stat-paused-plots"     label="Paused"           value={stats?.counts?.paused_plots ?? "-"}     tone="warn" />
        <LinkStat to="/invoices?overdue=1"                 testId="stat-overdue"          label="Overdue inv."     value={stats?.counts?.overdue_invoices ?? "-"} tone="bad"/>
        <LinkStat to="/plan-requests"                      testId="stat-pending-approvals" label="Pending approvals" value={(stats?.counts?.pending_plan_changes || 0) + (stats?.counts?.pending_pause_approvals || 0)} tone="warn"/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <LinkStat to="/vendors"                            testId="stat-vendors"           label="Vendors"              value={stats?.counts?.vendors ?? "-"} />
        <LinkStat to="/expenses"                           testId="stat-exp-this-month"    label="Expenses this month"  value={fmtINR(stats?.totals?.expenses_this_month)} />
        <LinkStat to="/expenses?status=pending"            testId="stat-vendor-payable"    label="Vendor payable"       value={fmtINR(stats?.totals?.vendor_payable)} tone="bad"/>
        <LinkStat to="/expenses?status=pending_approval"   testId="stat-exp-awaiting"      label="Bills awaiting approval" value={stats?.counts?.expenses_awaiting_approval ?? "-"} tone="warn"/>
      </div>
    </div>
  );
}
